import {
  buildManufacturingTransfersWorkbenchPath,
  buildManufacturingWorkOrderDetailPath,
  buildMrpAdjustmentDetailPath,
  buildMrpPurchaseInvoiceDetailPath,
  buildMrpPurchaseOrderDetailPath,
  buildSalesOrderDetailPath,
} from "@/lib/hubEntityDetailPaths";
import { buildInboundReceiptReceiveHref } from "@/pages/shipping/inboundReceiveNavigation";
import {
  megaventoryAdjustmentDocumentUrl,
  megaventoryGoodsTransferDocumentUrl,
  megaventoryPurchaseInvoiceDocumentUrl,
  megaventoryPurchaseOrderDocumentUrl,
  megaventorySalesOrderDocumentUrl,
} from "@/lib/megaventoryDocumentUrls";
import { megaventoryUrlFor, workOrderHubHref } from "@/pages/manufacturing/workOrdersPageModel";

import type { InventoryDiscrepancySkuContextRow } from "./inventoryDiscrepancyApi";

export type InventoryDiscrepancyContextReferenceMegaventoryLink = {
  href: string;
  ariaLabel: string;
};

export type InventoryDiscrepancyContextReferenceHubLink = {
  to: string;
  prefetchBuildOrderId?: number;
};

export type InventoryDiscrepancyContextReferenceResolution = {
  label: string;
  hoverHintKey: string;
  hub?: InventoryDiscrepancyContextReferenceHubLink;
  megaventory?: InventoryDiscrepancyContextReferenceMegaventoryLink;
};

function parsePositiveInt(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === "" || !/^\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseTransferOrderIdFromReference(reference: string | null): number | null {
  if (reference == null) return null;
  const match = /^T-0*(\d+)\b/i.exec(reference.trim());
  if (match == null) return null;
  return parsePositiveInt(match[1] ?? null);
}

const LEGACY_TRANSFER_REFERENCE_PREFIX = "Legacy-";

function isLegacyTransferReference(reference: string | null | undefined): boolean {
  return reference?.trim().startsWith(LEGACY_TRANSFER_REFERENCE_PREFIX) ?? false;
}

function legacyTransferMvDocumentLabel(reference: string): string {
  if (isLegacyTransferReference(reference)) {
    return reference.trim().slice(LEGACY_TRANSFER_REFERENCE_PREFIX.length);
  }
  return reference;
}

function parseLegacyTransferMvEntityId(
  reference: string | null | undefined,
  mvEntityId: string | null | undefined,
): number | null {
  const fromMvEntityId = parsePositiveInt(mvEntityId);
  if (fromMvEntityId != null) {
    return fromMvEntityId;
  }
  if (isLegacyTransferReference(reference)) {
    const suffix = reference!.trim().slice(LEGACY_TRANSFER_REFERENCE_PREFIX.length).trim();
    return parsePositiveInt(suffix);
  }
  return null;
}

function isLegacyMvTransferRow(
  row: Pick<InventoryDiscrepancySkuContextRow, "kind" | "reference" | "entity_id" | "mv_entity_id">,
): boolean {
  if (row.kind !== "transfer_in" && row.kind !== "transfer_out") return false;
  if (row.entity_id != null && row.entity_id.trim() !== "") return false;
  if (isLegacyTransferReference(row.reference)) return true;
  return row.mv_entity_id != null && row.mv_entity_id.trim() !== "";
}

function megaventoryLink(href: string, ariaLabel: string): InventoryDiscrepancyContextReferenceMegaventoryLink {
  return { href, ariaLabel };
}

function workOrderMegaventoryLink(
  mvEntityId: number,
  label: string,
): InventoryDiscrepancyContextReferenceMegaventoryLink | undefined {
  const href = megaventoryUrlFor({ mv_id: mvEntityId, mv_number: label });
  if (href == null) return undefined;
  return megaventoryLink(href, `Open ${label} in Megaventory`);
}

export function resolveInventoryDiscrepancyContextReference(
  row: InventoryDiscrepancySkuContextRow,
): InventoryDiscrepancyContextReferenceResolution | null {
  const label = row.reference?.trim();
  if (label == null || label === "") {
    return null;
  }

  const entityId = parsePositiveInt(row.entity_id);
  const mvEntityId = parsePositiveInt(row.mv_entity_id);
  const entityRef = row.entity_ref?.trim() ?? "";

  switch (row.kind) {
    case "purchase_order": {
      const purchaseOrderId = entityId;
      const hub =
        purchaseOrderId != null
          ? { to: buildMrpPurchaseOrderDetailPath(purchaseOrderId) }
          : undefined;
      const megaventory =
        mvEntityId != null
          ? megaventoryLink(
              megaventoryPurchaseOrderDocumentUrl(mvEntityId, label),
              `Open purchase order ${label} in Megaventory`,
            )
          : undefined;
      if (hub == null && megaventory == null) return null;
      return {
        label,
        hoverHintKey: hub != null ? "purchase_order_hub" : "purchase_order_mv",
        hub,
        megaventory,
      };
    }
    case "sales_order": {
      const salesOrderId = entityId;
      const hub =
        salesOrderId != null ? { to: buildSalesOrderDetailPath(salesOrderId) } : undefined;
      const megaventory =
        mvEntityId != null
          ? megaventoryLink(
              megaventorySalesOrderDocumentUrl(mvEntityId, label),
              `Open sales order ${label} in Megaventory`,
            )
          : undefined;
      if (hub == null && megaventory == null) return null;
      return {
        label,
        hoverHintKey: hub != null ? "sales_order_hub" : "sales_order_mv",
        hub,
        megaventory,
      };
    }
    case "purchase_invoice": {
      const purchaseInvoiceId = entityId;
      const purchaseOrderId = parsePositiveInt(entityRef);
      const hub =
        purchaseInvoiceId != null
          ? { to: buildMrpPurchaseInvoiceDetailPath(purchaseInvoiceId) }
          : undefined;
      const megaventory =
        mvEntityId != null
          ? megaventoryLink(
              megaventoryPurchaseInvoiceDocumentUrl(mvEntityId, label),
              `Open purchase invoice ${label} in Megaventory`,
            )
          : undefined;
      if (hub == null && megaventory == null) return null;
      return {
        label,
        hoverHintKey:
          hub != null && purchaseOrderId != null
            ? "purchase_invoice_po"
            : hub != null
              ? "purchase_invoice_hub"
              : "purchase_invoice_mv",
        hub,
        megaventory,
      };
    }
    case "transfer_in":
    case "transfer_out":
    case "transfer_pack": {
      if (isLegacyMvTransferRow(row)) {
        const legacyMvEntityId = parseLegacyTransferMvEntityId(label, row.mv_entity_id);
        if (legacyMvEntityId != null) {
          return {
            label,
            hoverHintKey: "legacy_transfer",
            megaventory: megaventoryLink(
              megaventoryGoodsTransferDocumentUrl(
                legacyMvEntityId,
                legacyTransferMvDocumentLabel(label),
              ),
              `Open goods transfer ${label} in Megaventory`,
            ),
          };
        }
      }
      const transferOrderId = entityId ?? parseTransferOrderIdFromReference(label);
      if (transferOrderId == null) return null;
      const documentNumber = entityRef !== "" ? entityRef : label;
      const megaventory =
        mvEntityId != null
          ? megaventoryLink(
              megaventoryGoodsTransferDocumentUrl(mvEntityId, documentNumber),
              `Open goods transfer ${documentNumber} in Megaventory`,
            )
          : undefined;
      return {
        label,
        hoverHintKey: "hub_transfer",
        hub: {
          to: buildManufacturingTransfersWorkbenchPath({
            transferOrderId,
            search: label,
          }),
        },
        megaventory,
      };
    }
    case "inbound_receipt": {
      const sourceType = row.entity_id?.trim() ?? "";
      const sourceId = entityRef;
      if (sourceType !== "" && sourceId !== "") {
        return {
          label,
          hoverHintKey: "inbound_receipt_source",
          hub: {
            to: buildInboundReceiptReceiveHref({
              sourceType,
              sourceId,
              receiptId: label,
              arrivalPackageId: row.arrival_package_id,
              arrivalPackageNo: row.arrival_package_no,
            }),
          },
        };
      }
      return {
        label,
        hoverHintKey: "inbound_receipt_queue",
        hub: {
          to: "/shipping/receive",
        },
      };
    }
    case "work_order":
    case "work_order_allocation": {
      let hub: InventoryDiscrepancyContextReferenceHubLink | undefined;
      if (entityId != null) {
        hub = {
          to: buildManufacturingWorkOrderDetailPath(entityId),
          prefetchBuildOrderId: entityId,
        };
      } else {
        const fallbackId = parsePositiveInt(label);
        if (fallbackId != null) {
          hub = {
            to: buildManufacturingWorkOrderDetailPath(fallbackId),
            prefetchBuildOrderId: fallbackId,
          };
        } else {
          hub = {
            to: workOrderHubHref({ number: label }),
          };
        }
      }
      const megaventory =
        mvEntityId != null ? workOrderMegaventoryLink(mvEntityId, label) : undefined;
      return {
        label,
        hoverHintKey: row.kind === "work_order_allocation" ? "work_order_allocation" : "work_order",
        hub,
        megaventory,
      };
    }
    case "adjustment":
    case "wastage":
    case "prototype": {
      const adjustmentId = entityId;
      const fullNumber = entityRef !== "" ? entityRef : label;
      const hub =
        adjustmentId != null ? { to: buildMrpAdjustmentDetailPath(adjustmentId) } : undefined;
      const mvId = mvEntityId ?? (entityRef === "" ? parsePositiveInt(label) : null);
      const href = mvId != null ? megaventoryAdjustmentDocumentUrl(mvId, fullNumber) : null;
      if (hub == null && href == null) {
        return null;
      }
      return {
        label,
        hoverHintKey: row.kind,
        hub,
        megaventory:
          href != null ? megaventoryLink(href, `Open ${label} in Megaventory`) : undefined,
      };
    }
    default:
      return null;
  }
}
